import spartaEventsUtility from "utilities/global/sparta-events-utility/2.3.5/js/sparta-events-utility";
import spartaGlobalFunctions from "utilities/global/sparta-global-functions-utility/2.1.3/js/sparta-global-functions-utility";
import _ from "utilities/vendor/lodash/4.17.21/js/lodash.custom.min";
import jQuery from "utilities/vendor/jquery/3.5.1/js/jquery";
import $ from "utilities/vendor/jquery/3.5.1/js/jquery";
/* eslint-disable no-underscore-dangle */

// Caches the viewport width.
let viewportWidth = $(window).width();

const EVENTS = {
  ELEMENT_UNSTICK: 'UNSTICK.spartaStickUtility',
  ELEMENT_STICK: 'STICK.spartaStickUtility',
  ELEMENT_FADE_IN: 'FADE_IN.spartaStickUtility',
  SPARTA_GLOBAL_READY: 'SPARTA_GLOBAL_READY.spartaBootstrapUtility',
};

const STICKY_CLASS = 'spa-sticky';

/**
 * Gets the total height of the page.
 * @param {object} body - A reference to `document.body`.
 * @param {object} html - A reference to `document.documentElement`.
 * @returns {number} - The page height in pixels.
 */
function _getPageHeight(body, html) {
  return Math.max(
    body.scrollHeight,
    body.offsetHeight,
    html.clientHeight,
    html.scrollHeight,
    html.offsetHeight,
  );
}

/**
 * Private helper function.
 * @param {object} html - A reference to `document.documentElement`.
 * @param {object} body - A reference to `document.body`.
 * @param {object} $el - A jQuery reference to the sticky element.
 * @returns {object} `stickPoint` and `unStickPoint` Y values
 */
function _getStickPoints(html, body, $el) {
  const $globalFooter = $('#globalFooterModule, #universalFooterModule');
  const pageHeight = _getPageHeight(body, html);
  const parent = $el.attr('data-parent');
  const $stickStartEl = $($el.attr('data-sticky-start'));
  const $stickEndEl = $($el.attr('data-sticky-end'));
  const stickyOverFooter = $el.attr('data-sticky-footer');
  const $startingPointEl = ($stickStartEl.length) ? $stickStartEl : $el;
  const stickPoint = Math.floor($startingPointEl.offset().top);
  let unStickPoint = $stickEndEl.length
    ? Math.round($stickEndEl.offset().top) - $el.height()
    : pageHeight;
  let limits = pageHeight;

  if (parent && parent !== 'body') {
    // if there's a parent specified, use the bottom of that element as the unstick point
    const $parent = $el.closest(parent);
    limits = Math.round($parent.height() - (stickPoint - $parent.offset().top));
    unStickPoint = Number.isNaN(stickPoint + limits) ? pageHeight : stickPoint + limits;
  }

  if (
    ((!parent || parent === 'body') && !$stickEndEl.length)
    && !stickyOverFooter
    && $globalFooter[0]
  ) {
    // unstick at the global footer
    unStickPoint = Math.round($globalFooter.offset().top) - $el.height();
  }

  return { stickPoint, unStickPoint };
}

/**
 * Class for sticky elements.
 */
class SpartaStickyEl {
  /**
   * @param {object} $win - A jQuery reference to the window object.
   * @param {object} body - A reference to `document.body`.
   * @param {object} html - A reference to `document.documentElement`.
   * @param {object} el - The DOM element to make sticky.
   * @param {number} level - The intended sticky level for this element.
   * @param {number} stickPoint - How far down the page to start sticking.
   * @param {number} unStickPoint - How far down the page to stop showing the element.
   * @param {number} [gap] - An offset to be subtracted from `unStickPoint`.
   */
  constructor($win, body, html, el, level, stickPoint, unStickPoint, gap = 150) {
    this.$el = $(el);
    this.$placeholder = null;
    this.$win = $win;
    this.body = body;
    this.el = el;
    this.gap = gap;
    this.html = html;
    this.level = level;
    this.stickPoint = stickPoint;
    this.unStickPoint = unStickPoint;
    this.fullWidth = el.getAttribute('data-sticky-full-width');
    this.fade = el.getAttribute('data-sticky-fade');
    this.levelOffset = this.getLevelOffset();

    this.addPlaceholder();
    this.initStyle();
  }

  /**
   * @returns {object} A collection of namespaced event strings for this utility.
   */
  get EVENTS() {
    return EVENTS;
  }

  /**
   * @returns {number} Sum of actively "stuck" element heights.
   */
  getLevelOffset() {
    let levelOffset = 0;
    for (let i = this.level - 1; i >= 0; i -= 1) {
      const levelEl = document.querySelector(`.${STICKY_CLASS}[data-sticky-level="${i}"].active`);
      if (levelEl && levelEl !== this.el) {
        levelOffset += levelEl.offsetHeight;
      }
    }
    return levelOffset;
  }

  /**
   * Sets an initial (hidden) style for the sticky element.
   * @returns {void}
   */
  initStyle() {
    if (this.$el.attr('data-sticky-fade')) {
      this.$el.addClass('hide');
    }
  }

  /**
   * Attaches a hidden placeholder element after the sticky element to prevent layout issues as the
   * element sticks and unsticks.
   * @returns {void}
   */
  addPlaceholder() {
    const fade = this.$el.attr('data-sticky-fade');
    // If the `data-placeholder` attribute is anything other than "false", the placeholder will
    // have the height of the sticky bar.
    const height = (this.$el.attr('data-placeholder') !== 'false') ? this.$el.outerHeight() : 0;
    const width = this.$el.width();

    this.$placeholder = fade
      ? $('<div class="sticky-placeholder-fade"></div>')
      : $('<div class="sticky-placeholder"></div>');
    if (!fade) {
      this.$placeholder.css({ width, height });
    }
    this.$el.after(this.$placeholder);
  }

  /**
   * Updates the placeholder element size.
   * @returns {void}
   */
  updatePlaceholder() {
    const width = this.el.offsetWidth;
    if (width) {
      this.$placeholder.css({ width });
    }

    const height = this.el.offsetHeight;
    if (height) {
      this.$placeholder.css({ height });
    }
  }

  /**
   * Removes the placeholder element. Called in conjunction with removing the sticky property of an
   * element.
   * @returns {void}
   */
  removePlaceholder() {
    this.$placeholder.remove();
  }

  /**
   * Resets the element's class/style and recalculates the stick point.
   * @param {number} dist current document Y position
   * @returns {void}
   */
  handleResize(dist) {
    this.$el.removeClass('active').attr('style', '');
    const { stickPoint, unStickPoint } = _getStickPoints(this.html, this.body, this.$el);

    this.stickPoint = stickPoint;
    this.unStickPoint = unStickPoint;
    this.updateSticky(dist);
    this.updatePlaceholder();
  }

  /**
   * Updates the attributes of the sticky element.
   * @param {number} dist - Current document Y position.
   * @returns {void}
   */
  updateSticky(dist) {
    const goOffScreen = this.unStickPoint - this.gap;
    const isCurrentlyStuck = this.$el.hasClass('active');
    const levelOffset = this.getLevelOffset();
    const stickPoint = this.stickPoint - levelOffset;
    const unStickPoint = this.unStickPoint - levelOffset;

    // Stick element.
    if (stickPoint <= dist && unStickPoint > dist) {
      // Trigger a custom event when an element intially sticks.
      if (!isCurrentlyStuck) {
        spartaEventsUtility.pub(EVENTS.ELEMENT_STICK, { $el: this.$el });
      }

      // Stick the element if it's newly sticky or if the offsets changed.
      if (!isCurrentlyStuck || this.getLevelOffset() !== this.levelOffset) {
        this.levelOffset = this.getLevelOffset();

        // Full-width elements.
        let width = (this.fullWidth) ? '100%' : `${this.$el.width()}px`;
        let left = (this.fullWidth) ? '0' : `${this.el.getBoundingClientRect().left}px`;

        // Fade-in elements are hidden, so calculate the width and position from the placeholder.
        if (this.fade) {
          this.$el.removeClass('hide');
          width = (this.fullWidth) ? '100%' : `${this.$placeholder[0].offsetWidth}px`;
          left = (this.fullWidth) ? '0' : `${this.$placeholder[0].getBoundingClientRect().left}px`;
        }

        // Calculate top position after fade elements have removed `.hide`.
        const top = `${levelOffset}px`;

        this.$el
          .css({ left, top, width })
          .addClass('active')
          .removeClass('fade-out');

        // Publish a Sparta event when the fade-in animation is complete.
        this.$el.on('animationend.sticky', (e) => {
          if (this.el.isEqualNode(e.target) && e.originalEvent.animationName === 'fadeIn') {
            spartaEventsUtility.pub(EVENTS.ELEMENT_FADE_IN, [$(e.target)]);
            this.$el.off('animationend.sticky');
          }
        });

        // Add the animating keyframe class.
        if (this.fade) {
          this.$el.addClass('fade-in');
        }

        return;
      }

      // If the element is already stuck, return early.
      if (this.$el.hasClass('active')) {
        return;
      }
    }

    // Below the stick point...
    if (dist > goOffScreen) {
      // Trigger a custom event when an element intially unsticks.
      if (isCurrentlyStuck) {
        spartaEventsUtility.pub(EVENTS.ELEMENT_UNSTICK, { $el: this.$el });
        this.$el
          .addClass('fade-out')
          .removeClass('active')
          .removeAttr('style');
        if (this.fade) {
          this.$el.addClass('hide');
        }
      }
    }

    // Above the stick point...
    if (dist < this.stickPoint) {
      // Trigger a custom event when an element intially unsticks.
      if (isCurrentlyStuck) {
        spartaEventsUtility.pub(EVENTS.ELEMENT_UNSTICK, { $el: this.$el });
        this.$el
          .removeClass('active')
          .removeClass('fade-out fade-in')
          .removeAttr('style');
        if (this.fade) {
          this.$el.addClass('hide');
        }
      }
    }
  }
}

/**
 * `initializeStickyModules` scans the DOM and creates a new `SpartaStickyEl` for each
 * `.spa-sticky` element. It also manages the `scroll` and `resize` window events.
 * @param {object} doc - A reference to `window.document`.
 * @param {object} win - A reference to the `window` object.
 * @param {object} els - A `nodeList` of `.spa-sticky` elements.
 */
const stickyApi = (function initializeStickyModules(doc, win, els) {
  const sticky = {
    /**
     * Caches values, sets up events, and creates instances.
     * @returns {void}
     */
    init() {
      // If this sticky has already been initialized, ignore any subsequent attempts.
      if (this.initialized) {
        return;
      }

      this.$win = $(win);
      this.body = doc.querySelector('body');
      this.html = doc.documentElement;
      this.isMobile = this.html.classList.contains('touch');
      this.stickyEls = [...els];
      this.stickyElsInstances = [];

      this.setupObservers();
      this.setupEvents();
      this.createStickyInstances();
      this.pageHeight = _getPageHeight(this.body, this.html);

      this.initialized = true;
    },

    /**
     * Gets the current Y position of the screen.
     * @returns {number} - The current scroll position in pixels.
     */
    getCurrentDist() {
      return (this.html && this.html.scrollTop) || document.body.scrollTop;
    },

    /**
     * Sets the `scroll` event on `window`. No throttling here allows the sticky items to stick
     * with no jumping. Sets `orientationchange` or `resize` events depending on mobile status. The
     * `orientationchange` event is further delayed to allow for all media query CSS to be applied
     * before recalculating positions. Events are namespaced for easy removal.
     * @returns {void}
     */
    setupEvents() {
      const boundResize = this.resizeHandler.bind(this);

      // Trigger resize when modals close.
      spartaEventsUtility.sub('UNFREEZE_SCROLL.spartaUILayersUtility', _.throttle(() => {
        _.delay(boundResize, 50);
      }, 500));

      // Trigger resize when a sticky element unsticks.
      spartaEventsUtility.sub(EVENTS.ELEMENT_UNSTICK, boundResize);

      this.$win.on('spartaWindowScroll', () => {
        if (this.pageHeight !== _getPageHeight(this.body, this.html)) {
          boundResize();
        }
        this.pageHeight = _getPageHeight(this.body, this.html);
      });

      if (this.isMobile) {
        this.$win.on('orientationchange.sticky', _.throttle(() => {
          _.delay(boundResize, 50);
        }, 500));
        this.$win.on('spartaWindowScroll', this.scrollHandler.bind(this));
      } else {
        this.$win.on('scroll.sticky touchmove.sticky', this.scrollHandler.bind(this));
        this.$win.on('resize.sticky', _.throttle(boundResize, 1000));

        // IE patch: When hitting the home key, we need to remove active state on all stickies
        // since the scroll handler is never invoked compared to other browsers.
        document.addEventListener('keydown', (e) => {
          const homeKeyCode = 36;
          const key = e.which || e.keyCode || 0;

          if (key === homeKeyCode) {
            this.removeActiveState();
          }
        });
      }
    },

    /**
     * Removes all namespaced sticky events.
     * @returns {void}
     */
    removeEvents() {
      this.$win.off('.sticky');
    },

    /**
     * Add an observer to each `.spa-sticky` to watch for DOM node removal.
     * @returns {void}
     */
    setupObservers() {
      if (window.MutationObserver) {
        // Observer for event cleanup.
        this.stickyEls.forEach((el) => {
          const observer = new MutationObserver(this.handleObserve.bind(this));
          observer.observe(el.parentElement, { childList: true });
        });
      }
    },

    /**
     * If a `.spa-sticky` node has been removed from the DOM, remove it from the
     * `stickyElsInstances` list so events don't try to call methods on the removed instance.
     * If there are no more instances on the page, remove all event listeners.
     * @param {object} record - The `MutationRecord` object to watch.
     * @returns {void}
     */
    handleObserve(record) {
      record.forEach((r) => {
        if (r.removedNodes.length) {
          // Loop through the removed nodes.
          const nodes = [...r.removedNodes];
          nodes.forEach((node) => {
            if (node.classList.contains(STICKY_CLASS)) {
              this.removeStickyInstances([node]);
            }
          });
        }
      });

      if (!this.stickyElsInstances.length) {
        this.removeEvents();
      }
    },

    /**
     * Handles the scroll event.
     * @returns {void}
     */
    scrollHandler() {
      this.updateStickyInstances();
    },

    /**
     * Handles the resize event. Loops through instances and fires their `resize` method, passing
     * the current Y position.
     * @returns {void}
     */
    resizeHandler() {
      // Cache the current window width.
      const newWidth = $(window).width();

      // IUf the width is unchanged, do nothing.
      if (viewportWidth !== newWidth) {
        // Otherwise, update the cached width, and run the resize code.
        viewportWidth = newWidth;
        const dist = this.getCurrentDist();
        this.stickyElsInstances.forEach((instance) => {
          instance.handleResize(dist);
        });
      }
    },

    /**
     * Loops through `.spa-sticky` elements and adds new `SpartaStickyEl` with initial values.
     * @param {array} [elsToCreate] - The sticky elements to create.
     * @returns {void}
     */
    createStickyInstances(elsToCreate = this.stickyEls) {
      elsToCreate.forEach((el) => {
        const $el = el.jquery ? el : $(el);
        const { stickPoint, unStickPoint } = _getStickPoints(this.html, this.body, $el);
        let level = $el.attr('data-sticky-level');
        let parent = $el.attr('data-sticky-parent');

        if (!level) {
          level = 1;
          $el.attr('data-sticky-level', level);
        }

        if (!parent) {
          parent = 'body';
          $el.attr('data-sticky-parent', parent);
        }

        // Add a class for lazy-loaded modules.
        $el.addClass(STICKY_CLASS);

        // Make sticky instances.
        this.stickyElsInstances.push(new SpartaStickyEl(
          this.$win,
          this.body,
          this.html,
          $el[0],
          level,
          stickPoint,
          unStickPoint,
        ));

        // If the page is loaded scrolled, update the sticky elements.
        if (this.getCurrentDist() > 0) {
          this.updateStickyInstances();
        }
      }, this);
    },

    /**
     * Loops through instances and removes sticky classes and styles.
     * @param {array} [elsToRemove] - The sticky elements to remove.
     * @returns {void}
     */
    removeStickyInstances(elsToRemove = this.stickyEls) {
      const elsArray = Array.isArray(elsToRemove) ? elsToRemove : [...elsToRemove];
      elsArray.forEach(($el) => {
        const el = $el.jquery ? $el[0] : $el;

        // Loop through sticky instances and remove the instance that matches the removed node.
        let indexFound = null;
        this.stickyElsInstances.some((instance, i) => {
          // TODO: Add `findIndex` polyfill.
          if (instance.el.isEqualNode(el)) {
            indexFound = i;
            return true;
          }
          return false;
        });
        if (indexFound !== null) {
          this.stickyElsInstances[indexFound].removePlaceholder();
          this.stickyElsInstances.splice(indexFound, 1);
          el.classList.remove(STICKY_CLASS, 'active');
          el.removeAttribute('style');
          spartaEventsUtility.pub(EVENTS.ELEMENT_UNSTICK, { $el: this.$el });
        }
      }, this);
    },

    /**
     * Loops through instances and calls their update method, passing the current Y position.
     * @param {number} dist - Current document Y position.
     * @returns {void}
     */
    updateStickyInstances(dist = this.getCurrentDist()) {
      if (!this.stickyElsInstances) {
        return;
      }

      this.stickyElsInstances.forEach((instance) => {
        instance.updateSticky(dist);
      });
    },

    /**
     * Loops through instances and calls their resize method, passing the current Y position.
     * @param {Number} dist - Current document Y position.
     * @returns {void}
     */
    recalcStickyInstances(dist = this.getCurrentDist()) {
      if (!this.stickyElsInstances) {
        return;
      }

      this.stickyElsInstances.forEach((instance) => {
        instance.handleResize(dist);
      });
    },

    /**
     * Removes the active state of the sticky.
     * @param {array} stickyEls - The sticky elements to loop through.
     * @returns {void}
     */
    removeActiveState(stickyEls = this.stickyEls) {
      stickyEls.forEach((el) => {
        const $el = el.jquery ? el : $(el);
        $el
          .removeClass('active')
          .removeClass('fade-out fade-in')
          .removeAttr('style');
      });
    },
  };

  // Initialize all sticky elements as soon as Sparta is ready.
  spartaEventsUtility.sub(EVENTS.SPARTA_GLOBAL_READY, () => {
    if (els.length) {
      sticky.init(els);
    }
  });

  return {
    addStickyEls: sticky.createStickyInstances.bind(sticky),
    removeStickyEls: sticky.removeStickyInstances.bind(sticky),
    updateStickyEls: sticky.updateStickyInstances.bind(sticky),
    recalcStickyEls: sticky.recalcStickyInstances.bind(sticky),
    EVENTS,
  };
}(document, window, document.querySelectorAll(`.${STICKY_CLASS}`)));

export default stickyApi;
